import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { useMemo } from 'react';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { useGetFileUrl } from '@/hooks/useFile';
import { describeRoomCombination } from '@/utils/estimates/getHotelRoomCombinationLabel';
import { getHotelCategoryLabel } from '@/utils/benefits/getHotelCategoryName';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formatPrice, sanitizeDeltaPrice } from '@/utils/circuits/prices/formatPrice';
import EstimateDayStepCardTemplate from './EstimateDayStepCardTemplate';
import {
  getRoomPensionOptions,
  toHotelPensionType,
} from '@/constants/hotelPension';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useConvertPriceOnlyIfDateISProvided } from '@/hooks/useCurrencyExchange';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';

interface EstimateDayStepHotelSummaryCardProps {
  step: CircuitDayFormData['steps'][number]['accommodations'];
  selectedOptions: Record<string, SelectedEstimateOption>;
  stepDates: {
    startDate: Date;
    endDate: Date;
  } | null;
  currency?: string;
  creationDate?: Date;
  accessToken?: string;
  language?: SupportedLocale;
}

const EstimateDayStepHotelSummaryCard = ({
  step,
  selectedOptions,
  stepDates,
  currency = 'EUR',
  creationDate,
  accessToken,
  language,
}: EstimateDayStepHotelSummaryCardProps) => {
  const { t } = useTranslation();
  const convertPrice = useConvertPriceOnlyIfDateISProvided(creationDate);
  const selectedHotelCategory = useMemo(
    () =>
      step.standardBenefit?.hotel?.HotelRoomCategory.find(
        ({ id }) => selectedOptions[step.id]?.hotelCategoryId === id,
      ),
    [selectedOptions, step.id, step.standardBenefit?.hotel?.HotelRoomCategory],
  );

  const selectedHotelRoomCombination = useMemo(() => {
    const roomCombination = selectedOptions[step.id]?.roomCombination;
    if (!roomCombination) return null;
    return describeRoomCombination(roomCombination, t);
  }, [selectedOptions, step.id, t]);

  const selectedHotelPensionType = useMemo(() => {
    const rawPensionType =
      selectedOptions[step.id]?.hotelPensionType ??
      step.standardBenefitPensionType ??
      step.standardBenefit?.hotel?.pricingPensionType;
    const stable = toHotelPensionType(rawPensionType ?? undefined);
    const options = getRoomPensionOptions(t);
    if (stable) {
      const label = options.find((o) => o.value === stable)?.label;
      if (label) return label;
    }
    return options.find((o) => o.value === 'SingleRoom')?.label as string;
  }, [
    selectedOptions,
    step.id,
    step.standardBenefitPensionType,
    step.standardBenefit?.hotel?.pricingPensionType,
    t,
  ]);

  const deltaPrice = useMemo(() => {
    const price = selectedOptions[step.id]?.deltaPrice;
    if (price == null) return price;
    const converted = convertPrice(price, 'EUR', currency);
    return sanitizeDeltaPrice(converted ?? price);
  }, [convertPrice, currency, selectedOptions, step.id]);

  const images = useMemo(() => {
    const hotelBenefit = step.standardBenefit;
    const standardImages = hotelBenefit?.hotel?.images ?? [];
    return hotelBenefit?.hotel?.mainImage
      ? [hotelBenefit?.hotel.mainImage, ...standardImages]
      : standardImages;
  }, [step.standardBenefit]);

  const mainImageKey = useMemo(() => {
    return images.length > 0 ? images[0] : null;
  }, [images]);

  const { data: imageUrl } = useGetFileUrl(mainImageKey, false, accessToken);

  const standardName = useMemo(() => {
    const translations = language
      ? step.standardBenefit?.translations?.[language]
      : undefined;
    const stepName = translations?.name ?? step.standardBenefit?.name;
    return stepName
      ? `${stepName} ${step.standardBenefit?.hotel?.category ? `- ${getHotelCategoryLabel(t, step.standardBenefit?.hotel?.category)}` : ''}`
      : '';
  }, [
    language,
    step.standardBenefit?.hotel?.category,
    step.standardBenefit?.name,
    step.standardBenefit?.translations,
    t,
  ]);

  return (
    <EstimateDayStepCardTemplate
      step={step}
      type={BenefitType.HOTEL}
      stepDates={stepDates}
      standardName={standardName}
      titlePrice={deltaPrice || undefined}
      displayOptionalBenefits={false}
      setDisplayOptionalBenefits={() => {}}
      imageUrl={imageUrl?.url}
      imagesKeys={images}
      shouldDisplayDetailsBtn={false}
      shouldDisplayReadMoreBtn={false}
      currency={currency}
      language={language}
      accessToken={accessToken}
      standardBenefitOptions={
        <EstimateDayStepHotelOptionsSummary
          selectedHotelRoomCombination={selectedHotelRoomCombination}
          selectedHotelCategoryName={selectedHotelCategory?.name}
          selectedHotelPensionType={selectedHotelPensionType}
          deltaPrice={deltaPrice}
          currency={currency}
        />
      }
    />
  );
};

export default EstimateDayStepHotelSummaryCard;

interface EstimateDayStepHotelOptionsSummaryProps {
  selectedHotelRoomCombination: string | null;
  selectedHotelCategoryName?: string | null;
  selectedHotelPensionType: string;
  deltaPrice: number;
  currency?: string;
}

const EstimateDayStepHotelOptionsSummary = ({
  selectedHotelRoomCombination,
  selectedHotelCategoryName,
  selectedHotelPensionType,
  deltaPrice,
  currency = 'EUR',
}: EstimateDayStepHotelOptionsSummaryProps) => {
  const { t } = useTranslation();

  const formattedDeltaPrice = useMemo(() => {
    if (!deltaPrice) return t('common.included');
    if (deltaPrice > 0)
      return `+ ${formatPrice(deltaPrice)} ${formatCurrency(currency)}`;
    return `${formatPrice(deltaPrice)} ${formatCurrency(currency)}`;
  }, [currency, deltaPrice, t]);

  return (
    <div className="flex flex-col gap-8 p-4 border border-brand-100 rounded-lg w-full">
      {selectedHotelRoomCombination && (
        <div className="flex flex-col gap-4 w-full">
          <h2 className="text-sm leading-6 font-semibold text-black">
            1. Répartition des voyageurs
          </h2>
          <RadioGroup className="flex flex-col gap-2" disabled>
            <div className="flex gap-4 items-center">
              <RadioGroupItem checked value="selected-room-combination" />
              <span className="text-sm font-normal text-gray-700">
                {selectedHotelRoomCombination}
              </span>
              <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
                {formattedDeltaPrice}
              </span>
            </div>
          </RadioGroup>
        </div>
      )}
      {selectedHotelCategoryName && (
        <div className="flex flex-col gap-4 w-full">
          <h2 className="text-sm leading-6 font-semibold text-black">
            2. Catégorie d'hébergement
          </h2>
          <RadioGroup className="flex flex-col gap-2" disabled>
            <div className="flex gap-4 items-center">
              <RadioGroupItem checked value="selected-room-category" />
              <span className="text-sm font-normal text-gray-700">
                {selectedHotelCategoryName}
              </span>
            </div>
          </RadioGroup>
        </div>
      )}
      <div className="flex flex-col gap-4 w-full">
        <h2 className="text-sm leading-6 font-semibold text-black">
          3. Type de pension
        </h2>
        <RadioGroup className="flex flex-col gap-2" disabled>
          <div className="flex gap-4 items-center">
            <RadioGroupItem checked value="selected-room-category" />
            <span className="text-sm font-normal text-gray-700">
              {selectedHotelPensionType}
            </span>
            {!selectedHotelRoomCombination && (
              <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
                {formattedDeltaPrice}
              </span>
            )}
          </div>
        </RadioGroup>
      </div>
    </div>
  );
};
